/**
 * Oct 20 (Issue 03B, FD-01) — the destinations a standalone "Move Vehicle" may offer, from the SAME reads Guided
 * Start and the Stop's parking question use (readGuidedStartContext → guidedBayChoices; readParkingSpotChoices).
 *
 * Pure and ADVISORY: occupancy here is a picture. The one canonical writer (move_vehicle_location →
 * apply_vehicle_location_locked) re-decides at commit, under locks, and a lost race comes back as a refusal.
 */
import { guidedBayChoices, whereWordsOf, type GuidedStartContext } from '@/lib/timer/interaction/guidedStart';
import type { ParkingSpotChoice } from '@/lib/location/api';
import type { VehicleLocationTarget } from '@/lib/location/model';

export type MoveDestinationGroup = 'bay' | 'spot' | 'other';

export interface MoveDestination {
  key: string;
  group: MoveDestinationGroup;
  label: string;
  target: VehicleLocationTarget;
  /** Null = selectable. Otherwise why not ("Where it is now", "Occupied"). */
  disabledReason: string | null;
}

const HERE = 'Where it is now';

export function moveDestinations(ctx: GuidedStartContext, spots: readonly ParkingSpotChoice[]): MoveDestination[] {
  const cur = ctx.current;
  const bays: MoveDestination[] = guidedBayChoices(ctx).map((b) => {
    const here = cur?.kind === 'BAY' && cur.bayId === b.id;
    return {
      key: `bay:${b.id}`, group: 'bay', label: b.label,
      target: { kind: 'BAY', bayId: b.id },
      disabledReason: here ? HERE : b.occupied ? 'Occupied' : null,
    };
  });
  const lots: MoveDestination[] = spots.map((s) => {
    const here = cur?.kind === 'PARKING_SPOT' && cur.parkingSpotId === s.id;
    return {
      key: `spot:${s.id}`, group: 'spot', label: `Spot ${s.label}`,
      target: { kind: 'PARKING_SPOT', parkingSpotId: s.id },
      disabledReason: here ? HERE : s.occupied ? 'Occupied' : null,
    };
  });
  const other: MoveDestination[] = [
    { key: 'general', group: 'other', label: 'General parking lot (no numbered spot)', target: { kind: 'ON_SITE_GENERAL' },
      disabledReason: cur?.kind === 'ON_SITE_GENERAL' ? HERE : null },
    { key: 'unknown', group: 'other', label: 'On site — exact spot unknown', target: { kind: 'ON_SITE_UNKNOWN_EXACT' },
      disabledReason: cur?.kind === 'ON_SITE_UNKNOWN_EXACT' ? HERE : null },
    { key: 'off:TEST_DRIVE', group: 'other', label: 'Off site — test drive', target: { kind: 'OFF_SITE', reason: 'TEST_DRIVE' }, disabledReason: null },
    { key: 'off:SUBLET', group: 'other', label: 'Off site — sublet', target: { kind: 'OFF_SITE', reason: 'SUBLET' }, disabledReason: null },
    { key: 'off:OTHER', group: 'other', label: 'Off site — other', target: { kind: 'OFF_SITE', reason: 'OTHER' }, disabledReason: null },
  ];
  return [...bays, ...lots, ...other];
}

/** "Move Judy Bloom’s 2003 Grand Prix from Bay 2 to Spot 12?" — the confirmation sentence. */
export function moveConfirmSentence(ctx: GuidedStartContext, dest: MoveDestination): string {
  const from = cur(ctx);
  return `Move ${ctx.vehicleLabel ?? 'this vehicle'} from ${from} to ${lowerFirstWord(dest.label)}?`;
}

function cur(ctx: GuidedStartContext): string {
  return whereWordsOf(ctx).from;
}

function lowerFirstWord(label: string): string {
  return /^(General|On site|Off site)/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label;
}

/**
 * What a move does NOT do, said once in the dialog (FD-01): it never completes the work, restarts a timer,
 * hands the ticket to the front office, or replaces the technician. Parking paused work releases only the bay.
 * Addendum L4: running work may go bay → bay with its timer still running; parking it still needs a pause or stop.
 */
export const MOVE_GUARANTEES =
  'Moving a vehicle never completes the work, restarts a timer, or changes who has the ticket. '
  + 'Running work can move from bay to bay with its timer still running; to park it, pause or stop it first. '
  + 'Paused work keeps its technician, who can resume it from the bay.';

/** Addendum L5 — the "who moved it" question, asked on every confirmation (never assumed). */
export type MoverChoice = { kind: 'self' } | { kind: 'onBehalf'; moverUserId: string | null };

/** The attestation a choice sends, or null while the choice is incomplete (Move stays disabled). */
export function moverAttestationOf(choice: MoverChoice | null): { kind: 'self' } | { kind: 'onBehalf'; moverUserId: string } | null {
  if (!choice) return null;
  if (choice.kind === 'self') return { kind: 'self' };
  return choice.moverUserId ? { kind: 'onBehalf', moverUserId: choice.moverUserId } : null;
}
